import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import participantsPaneTheme from '../../../base/components/themes/participantsPaneTheme.json';
import useContextMenu from '../../../base/ui/hooks/useContextMenu.web';
import { normalizeAccents } from '../../../base/util/strings.web';
import { isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { isCurrentRoomRenamable } from '../../functions';
import { useParticipantDrawer } from '../../hooks';
import RenameButton from '../breakout-rooms/components/web/RenameButton';
import MeetingParticipantContextMenu from './MeetingParticipantContextMenu';
import MeetingParticipantItems from './MeetingParticipantItems';
import { SearchInput } from "common/components/SearchInput";
import { InviteButton } from "../../../../11sight/features/invite/components/add-people-dialog/web/InviteButton";
import { withPixelLineHeight } from "../../../base/styles/functions.web";
import { isButtonEnabled, showOverflowDrawer } from "../../../toolbox/functions.web";
import { getParticipantById, isScreenShareParticipant } from "../../../base/participants/functions";
import { getBreakoutRooms, getCurrentRoomId } from "../../../breakout-rooms/functions";
import { getSortedParticipantIds, shouldRenderInviteButton } from "../../functions";
import { getVisitorsCount } from "../../../visitors/functions";
const useStyles = makeStyles()(theme => {
    return {
        headingW: {
            color: theme.palette.warning02
        },
        heading: {
            color: theme.palette.text02,
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(4),
            [`@media(max-width: ${participantsPaneTheme.MD_BREAKPOINT})`]: {
                ...withPixelLineHeight(theme.typography.bodyShortBoldLarge)
            }
        }
    };
});
/**
 * Renders the MeetingParticipantList component.
 * NOTE: This component is not using useSelector on purpose. The child components MeetingParticipantItem
 * and MeetingParticipantContextMenu are using connect. Having those mixed leads to problems.
 * When this one was using useSelector and the other two were not -the other two were re-rendered before this one was
 * re-rendered, so when participant is leaving, we first re-render the item and menu components,
 * throwing errors (closing the page) before removing those components for the participant that left.
 *
 * @returns {ReactNode} - The component.
 */
function MeetingParticipants({ currentRoom, overflowDrawer, participantsCount, searchString, setSearchString, showInviteButton, sortedParticipantIds = [], visitorsCount }) {
    const { t } = useTranslation();
    const [lowerMenu, , toggleMenu, menuEnter, menuLeave, raiseContext] = useContextMenu();
    const [drawerParticipant, closeDrawer, openDrawerForParticipant] = useParticipantDrawer();
    // FIXME:
    // It seems that useTranslation is not very scalable. Unmount 500 components that have the useTranslation hook is
    // taking more than 10s. To workaround the issue we need to pass the texts as props. This is temporary and dirty
    // solution!!!
    // One potential proper fix would be to use react-window component in order to lower the number of components
    // mounted.
    const participantActionEllipsisLabel = t('participantsPane.actions.moreParticipantOptions');
    const youText = t('chat.you');
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const _isCurrentRoomRenamable = useSelector(isCurrentRoomRenamable);
    const { classes: styles } = useStyles();
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { "aria-level": 1, className: "sr-only", role: "heading" }, t("participantsPane.title")),
        visitorsCount > 0 && (React.createElement("div", { className: cx(styles.heading, styles.headingW) }, t("participantsPane.headings.visitors", {
            count: visitorsCount
        }))),
        showInviteButton && React.createElement(InviteButton, null),
        React.createElement("div", { className: styles.heading },
            currentRoom?.name ? `${currentRoom.name} (${participantsCount})` : t("participantsPane.headings.participants", {
                count: participantsCount
            }),
            currentRoom?.name && _isCurrentRoomRenamable && (React.createElement(RenameButton, { breakoutRoomJid: currentRoom?.jid, name: currentRoom?.name }))),
        React.createElement(SearchInput, { id: "participants-search-input", onChange: setSearchString, placeholder: t("participantsPane.search"), value: searchString }),
        React.createElement("div", null,
            React.createElement(MeetingParticipantItems, { isInBreakoutRoom: isBreakoutRoom, lowerMenu: lowerMenu, openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: overflowDrawer, participantActionEllipsisLabel: participantActionEllipsisLabel, participantIds: sortedParticipantIds, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), toggleMenu: toggleMenu, youText: youText })),
        React.createElement(MeetingParticipantContextMenu, { closeDrawer: closeDrawer, drawerParticipant: drawerParticipant, offsetTarget: raiseContext?.offsetTarget, onEnter: menuEnter, onLeave: menuLeave, onSelect: lowerMenu, overflowDrawer: overflowDrawer, participantID: raiseContext?.entity })));
}
function _mapStateToProps(state) {
    let sortedParticipantIds = getSortedParticipantIds(state);
    sortedParticipantIds = sortedParticipantIds.filter((id) => {
        const participant = getParticipantById(state, id);
        return !isScreenShareParticipant(participant);
    });
    const participantsCount = sortedParticipantIds.length;
    const visitorsCount = getVisitorsCount(state);
    const showInviteButton = shouldRenderInviteButton(state) && isButtonEnabled("invite", state);
    const overflowDrawer = showOverflowDrawer(state);
    const currentRoomId = getCurrentRoomId(state);
    const currentRoom = getBreakoutRooms(state)[currentRoomId];
    return {
        currentRoom,
        overflowDrawer,
        participantsCount,
        showInviteButton,
        sortedParticipantIds,
        visitorsCount
    };
}
export default connect(_mapStateToProps)(MeetingParticipants);
